import { CATEGORY_SETUP, useEditor } from '../store/useEditor'
import { pathLength, toPx } from '../lib/geometry'

/**
 * The only thing on the canvas while you are drawing.
 *
 * It answers the two questions a contractor actually has: what am I supposed
 * to do, and how long is this run. There is no third question about how to
 * finish, because on a two-point run the second click finishes it.
 *
 * On a multi-corner shape - garland round a garage door - there IS a third
 * question, so it gets a big Done target here at the bottom of the photo. That
 * is where a thumb already rests on a phone or a tablet, it never covers the
 * roof, and it disappears the moment the run is committed.
 */
export default function DrawHud() {
  const doc = useEditor((s) => s.doc)
  const draft = useEditor((s) => s.draft)
  const hover = useEditor((s) => s.hover)
  const tool = useEditor((s) => s.tool)
  const category = useEditor((s) => s.category)
  const cancelDraft = useEditor((s) => s.cancelDraft)
  const commitDraft = useEditor((s) => s.commitDraft)

  const photo = doc.photo
  if (!photo) return null

  /*
   * NOTHING ON THE PHOTO UNTIL YOU ARE ACTUALLY DRAWING.
   *
   * A permanent "Ready to draw" banner explaining the gesture sat across the
   * bottom of every mockup, including the ones being shown to a customer. The
   * blurb already lives in the panel next to the category button that sets it,
   * which is where someone looks when they are choosing what to light. Over the
   * house it is just something covering the house.
   */
  const drawing = !!draft && draft.length > 0
  if (!drawing) return null

  const ppf = doc.calibration.pixelsPerFoot
  const feet = (px: number) => (ppf ? px / ppf : null)

  // Live length of the segment being placed right now, and of the run so far.
  let segFt: number | null = null
  let runFt: number | null = null
  if (draft && draft.length) {
    const pts = draft.map((p) => toPx(p, photo.width, photo.height))
    runFt = feet(pathLength(pts))
    if (hover && draft.length) {
      const last = pts[pts.length - 1]
      const h = toPx(hover, photo.width, photo.height)
      segFt = feet(Math.hypot(h.x - last.x, h.y - last.y))
      if (runFt !== null && segFt !== null) runFt += segFt
    }
  }

  const fmt = (v: number | null) => (v === null ? null : `${v.toFixed(1)} ft`)

  return (
    <div className="draw-hud">
      <>
          <div className="hud-nums">
            <span className="hud-tip">
              {CATEGORY_SETUP[category].multi
                ? draft && draft.length > 1
                  ? 'Tap the last corner again to finish'
                  : 'Click the next corner'
                : 'Click where it ends'}
            </span>
            {/*
              No measurements while drawing, for now. This is a visual design
              tool until the footage has been validated in the field, and a
              running total in feet is the most quotable thing on the screen.
              The numbers are still computed underneath; nothing here reads
              them.
            */}
          </div>

          {/*
            A two-point run still gets no Finish button: the second click ends
            it, and a button claiming otherwise would misdescribe the tool.
            A multi-corner shape gets a real one, because it genuinely has to
            be told when to stop. Tapping the last corner again does the same
            thing - this is the visible half of that, for the person who has
            not discovered the gesture and should not have to.
          */}
          <div className="hud-actions">
            <button className="btn ghost" onClick={cancelDraft}>
              Cancel
            </button>
            {CATEGORY_SETUP[category].multi && draft && draft.length > 1 && (
              <button className="btn primary hud-done" onClick={commitDraft}>
                Done
              </button>
            )}
          </div>
      </>
    </div>
  )
}
